import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './Home.module.css';
import heroImg from '../../assets/images/gallery_1.jpg';
import heroSlide1 from '../../assets/images/hero_slide_1.png';
import heroSlide2 from '../../assets/images/hero_slide_2.png';
import heroSlide3 from '../../assets/images/hero_slide_3.png';
import airHostessImg from '../../assets/images/Air Hostess Training.png';
import airportMgmtImg from '../../assets/images/Airport Management.png';
import customerServiceImg from '../../assets/images/Customer Service Executive.png';

const HERO_IMAGES = [heroImg, heroSlide1, heroSlide2, heroSlide3];

/* ── Intersection Observer hook for scroll reveals ── */
const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('visible'); obs.unobserve(e.target); } });
    }, { threshold: 0.12 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
};

/* ══ HOME PAGE ════════════════════════════════════════════════ */
const Home = () => {
  useReveal();
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % HERO_IMAGES.length);
    }, 4500);
    return () => clearInterval(timer);
  }, []);

  return (
    <main className={styles.home}>

      {/* ──────────────────────────────────────────────────────────
          SECTION 1 — HERO
      ────────────────────────────────────────────────────────── */}
      <section className={styles.hero} data-theme="dark">
        {HERO_IMAGES.map((img, i) => (
          <img 
            key={i} 
            src={img} 
            alt={`Hero Background ${i + 1}`} 
            className={`${styles.heroBgImg} ${i === currentSlide ? styles.active : ''}`} 
          />
        ))}
        <div className={styles.heroOverlay} />

        <div className={`container ${styles.heroContent}`}>


          <h1 className={styles.heroTitle}>
            <span className={`${styles.heroLine} reveal`}>START</span>
            <span className={`${styles.heroLine} ${styles.heroLineBlue} reveal reveal-delay-1`}>YOUR CAREER WITH</span>
            <span className={`${styles.heroLineSub} reveal reveal-delay-2`}>VIN FLY AVIATION ✈</span>
          </h1>

          <p className={`${styles.heroDesc} reveal reveal-delay-3`}>
            Air Hostess | Airport Management | Customer Service Executive
          </p>

          <div className={`${styles.heroCtas} reveal reveal-delay-4`}>
            <a
              href="https://forms.zohopublic.in/vinflyaviationacademy1/form/Inquiryfrom/formperma/ECKFsrPVmWZroHhauO4Q68BX9OqBDCLasFE_EaGzvcs"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              APPLY NOW →
            </a>
            <Link to="/courses" className={styles.heroGhostBtn}>EXPLORE COURSES</Link>
          </div>
        </div>

        {/* Floating stats panel */}
        <div className={styles.heroStats}>
          <div className={styles.heroStatItem}>
            <span className={styles.heroStatNum}>100%</span>
            <span className={styles.heroStatLabel}>PLACEMENT ASSISTANCE</span>
          </div>
        </div>


      </section>

      {/* ──────────────────────────────────────────────────────────
          SECTION 2 — FEATURES BAR
      ────────────────────────────────────────────────────────── */}
      <section className={styles.statsStrip}>
        <div className="container">
          <p className={`label ${styles.statsLabel}`}>WHY CHOOSE US</p>
          <div className={styles.statsRow}>
            <div className={styles.statItem}>
              <div className={styles.statNum} style={{ fontSize: '2rem' }}>✔ 100%</div>
              <p className={styles.statLabel}>Placement Assistance</p>
              <p className={styles.statSub}>We guarantee job placement support with top airlines and airports across India.</p>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <div className={styles.statNum} style={{ fontSize: '2rem' }}>✔ 10+ Yr</div>
              <p className={styles.statLabel}>Experienced Trainers</p>
              <p className={styles.statSub}>Learn from industry veterans with decades of aviation experience.</p>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <div className={styles.statNum} style={{ fontSize: '2rem' }}>✔ Real</div>
              <p className={styles.statLabel}>Practical Training</p>
              <p className={styles.statSub}>Hands-on training with mock aircraft cabins and real-world simulations.</p>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <div className={styles.statNum} style={{ fontSize: '2rem' }}>✔ EMI</div>
              <p className={styles.statLabel}>Affordable Fees</p>
              <p className={styles.statSub}>Quality education at competitive prices with flexible payment options.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────
          SECTION 3 — COURSES
      ────────────────────────────────────────────────────────── */}
      <section className={`${styles.courses} section-py`}>
        <div className="container">
          <div className={`${styles.coursesHeader} reveal`}>
            <div>
              <p className="label" style={{ marginBottom: 12, fontSize: 'clamp(14px, 3vw, 22px)', letterSpacing: 'clamp(3px, 1vw, 8px)' }}>OUR PROGRAMS</p>
              <h2 className="section-title">Choose Your <span className="accent">Aviation Path.</span></h2>
              <p className={styles.fleetSub}>Launch your career in the exciting world of aviation with our industry-recognized courses</p>
            </div>
            <Link to="/courses" className={styles.viewAll}>View All Courses →</Link>
          </div>

          <div className={styles.courseGrid}>
            {COURSES.map((c, i) => (
              <div key={c.name} className={`${styles.courseCard} ${c.featured ? styles.featured : ''} dark-card reveal reveal-delay-${(i % 3) + 1}`}>
                <div className={styles.cardTop} style={{ background: c.featured ? 'linear-gradient(135deg,#1558C0,#1A73E8)' : 'linear-gradient(135deg,#D0E4FF,#AACAF5)' }}>
                  {c.image && <img src={c.image} alt={c.name} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }} />}
                  <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: c.image ? 'linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.75))' : 'transparent', zIndex: 0 }} />
                  <span className={styles.cardDuration} style={{ zIndex: 1, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>{c.duration}</span>
                  {!c.image && <div className={styles.cardIcon} style={{ zIndex: 1 }}>{c.icon}</div>}
                  {c.featured && <span className={`badge badge-filled-cyan ${styles.popularBadge}`} style={{ zIndex: 1 }}>MOST POPULAR</span>}
                </div>
                <div className={styles.cardBody}>
                  <h3 className={styles.cardTitle}>{c.name}</h3>
                  <div className={styles.cardDivider} />
                  <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginBottom: '1rem', color: 'var(--text-muted)' }}>
                    {c.points.map((p, idx) => <li key={idx} style={{ marginBottom: '0.5rem' }}>{p}</li>)}
                  </ul>
                  <div className={styles.factItem}><span className={styles.factLabel}>Starting Salary:</span><span className={styles.factVal} style={{ fontSize: '0.9rem' }}>{c.salary}</span></div>
                  <Link to="/courses" className={styles.cardCta} style={{ marginTop: '1.5rem' }}>View Details →</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────
          SECTION 4 — TESTIMONIALS
      ────────────────────────────────────────────────────────── */}
      <section className={`${styles.testimonials} section-py`}>
        <div className="container">
          <div className={`section-header reveal`}>
            <p className="label" style={{ fontSize: 'clamp(14px, 3vw, 22px)', letterSpacing: 'clamp(3px, 1vw, 8px)' }}>TESTIMONIALS</p>
            <h2 className="section-title" style={{ marginTop: 12 }}>
              What Our <span className="accent">Students Say.</span>
            </h2>
            <p className={styles.fleetSub} style={{ marginTop: '0.5rem' }}>Hear from our graduates who have successfully launched their aviation careers</p>
          </div>
          <div className={styles.testGrid}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className={`${styles.testCard} dark-card reveal reveal-delay-${i + 1}`}>
                <span className={styles.testQuote}>&ldquo;</span>
                <p className={styles.testText}>{t.text}</p>
                <div className={styles.testStars}>★★★★★</div>
                <div className={styles.testAuthor}>
                  <span className={styles.testName}>{t.name}</span>
                  <span className={styles.testRole}>{t.role}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────
          SECTION 5 — CTA BANNER
      ────────────────────────────────────────────────────────── */}
      <section className={styles.ctaBanner} data-theme="dark">
        <div className={styles.ctaPlane}>✈</div>
        <div className={`container ${styles.ctaContent}`}>
          <h2 className={styles.ctaTitle}>Ready to Fly High?</h2>
          <div className={styles.ctaBtns}>
            <a
              href="https://forms.zohopublic.in/vinflyaviationacademy1/form/AdmissionInquiryfrom/formperma/V2QuQqnDjF5c9UPIMPbrruVOtRC_W48NPISG0PaJ3lo"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.ctaBtnWhite}
            >
              Start Your Application
            </a>
            <Link to="/courses" className={styles.ctaBtnGhost}>Download Brochure</Link>
          </div>
        </div>
      </section>

    </main>
  );
};

/* ── DATA ─────────────────────────────────────────────────────── */
const COURSES = [
  { name: 'Air Hostess Training', duration: '12 Months', salary: '₹25,000 - ₹60,000/month', points: ['Grooming & Personality Development', 'In-Flight Service Training', 'Safety & Emergency Procedures'], image: airHostessImg, featured: true },
  { name: 'Airport Management', duration: '12 Months', salary: '₹20,000 - ₹45,000/month', points: ['Ground Handling Operations', 'Passenger Handling', 'Airport Security Protocols'], image: airportMgmtImg, featured: false },
  { name: 'Customer Service Executive', duration: '12 Months', salary: '₹18,000 - ₹35,000/month', points: ['Communication Skills', 'Conflict Resolution', 'Customer Relationship Management'], image: customerServiceImg, featured: false },
];

const TESTIMONIALS = [
  { text: `VIN Fly Academy transformed my dream into reality. The practical training and placement support helped me land my job at a leading airline within 2 months of completing the course!`, name: 'Priya Sharma', role: 'Air Hostess Graduate ⭐⭐⭐⭐⭐' },
  { text: `The trainers here are exceptional. Their industry experience and personalized attention made all the difference. I am now working at Pune International Airport.`, name: 'Rahul Patel', role: 'Airport Management Graduate ⭐⭐⭐⭐⭐' },
  { text: `Best decision I ever made! The course was comprehensive, affordable, and the placement team was incredibly supportive throughout my job search.`, name: 'Ananya Desai', role: 'Customer Service Executive ⭐⭐⭐⭐⭐' },
];

export default Home;
